import { Link } from "react-router-dom";
import { ArrowRight, Phone } from "lucide-react";
import { useEffect, useRef } from "react";
import { CTA_DEVIS } from "@/data/forms";

/* ─── Floating particles canvas ─── */
function ParticlesBg() {
  const canvasRef = useRef(null);
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    let animId;
    const particles = [];
    const resize = () => { canvas.width = canvas.offsetWidth; canvas.height = canvas.offsetHeight; };
    resize();
    window.addEventListener("resize", resize);
    for (let i = 0; i < 60; i++) {
      particles.push({
        x: Math.random() * canvas.width,
        y: Math.random() * canvas.height,
        r: Math.random() * 1.5 + 0.3,
        dx: (Math.random() - 0.5) * 0.3,
        dy: (Math.random() - 0.5) * 0.3,
        o: Math.random() * 0.5 + 0.1,
      });
    }
    const draw = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      particles.forEach(p => {
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(139,0,0,${p.o})`;
        ctx.fill();
        p.x += p.dx; p.y += p.dy;
        if (p.x < 0 || p.x > canvas.width) p.dx *= -1;
        if (p.y < 0 || p.y > canvas.height) p.dy *= -1;
      });
      animId = requestAnimationFrame(draw);
    };
    draw();
    return () => { cancelAnimationFrame(animId); window.removeEventListener("resize", resize); };
  }, []);
  return <canvas ref={canvasRef} className="hero-particles" style={{ width: '100%', height: '100%' }} />;
}

/* ─── Wave SVG divider ─── */
function WaveDivider() {
  return (
    <div style={{ position: 'absolute', bottom: -1, left: 0, width: '100%', overflow: 'hidden', lineHeight: 0, zIndex: 10 }}>
      <svg viewBox="0 0 1440 80" preserveAspectRatio="none" style={{ width: '100%', height: '60px', display: 'block' }}>
        <path d="M0,40 C360,80 720,0 1080,40 C1260,60 1380,30 1440,40 L1440,80 L0,80 Z" fill="#ffffff" />
        <path d="M0,50 C240,70 480,20 720,50 C960,80 1200,30 1440,50 L1440,80 L0,80 Z" fill="#ffffff" opacity="0.5" />
      </svg>
    </div>
  );
}

/* ─── Ripple on click ─── */
function handleRipple(e) {
  const btn = e.currentTarget;
  const circle = document.createElement("span");
  const rect = btn.getBoundingClientRect();
  circle.className = "ripple-circle";
  circle.style.left = `${e.clientX - rect.left - 10}px`;
  circle.style.top = `${e.clientY - rect.top - 10}px`;
  btn.appendChild(circle);
  setTimeout(() => circle.remove(), 600);
}

export default function HeroPremium({ badge, titleWhite, titleRed, photo, photoAlt, photoBadgeIcon: BadgeIcon, photoBadgeTitle, photoBadgeSub, decoNumber }) {
  const photoRef = useRef(null);

  /* Parallax on scroll */
  useEffect(() => {
    const handleScroll = () => {
      if (photoRef.current) {
        const y = window.scrollY;
        photoRef.current.style.transform = `translateY(${y * 0.08}px)`;
      }
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <section data-testid="hero-premium" style={{ backgroundColor: '#0a0a0a', position: 'relative', overflow: 'hidden' }}>
      {/* Grain texture */}
      <div style={{ position: 'absolute', inset: 0, opacity: 0.04, backgroundImage: 'url("data:image/svg+xml,%3Csvg viewBox=\'0 0 256 256\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cfilter id=\'n\'%3E%3CfeTurbulence type=\'fractalNoise\' baseFrequency=\'0.9\' numOctaves=\'4\' stitchTiles=\'stitch\'/%3E%3C/filter%3E%3Crect width=\'100%25\' height=\'100%25\' filter=\'url(%23n)\'/%3E%3C/svg%3E")', backgroundSize: '128px', zIndex: 0 }} />

      {/* Floating particles */}
      <ParticlesBg />

      {/* Decorative background number */}
      {decoNumber && <span className="deco-number">{decoNumber}</span>}

      <div style={{ maxWidth: '1360px', margin: '0 auto', padding: '60px 32px 80px', position: 'relative', zIndex: 2 }}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '48px', alignItems: 'center' }} className="lg:!grid-cols-[55%_45%]">

          {/* LEFT — Text */}
          <div>
            {/* Badge pill */}
            <div className="animate-fade-in-up" style={{ animationDelay: '0.1s', animationFillMode: 'forwards', opacity: 0, marginBottom: '28px' }}>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', backgroundColor: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: '100px', padding: '8px 20px', fontSize: '13px', color: 'rgba(255,255,255,0.7)', letterSpacing: '0.02em', backdropFilter: 'blur(8px)' }}>
                {badge}
              </span>
            </div>

            {/* Title with animated red line */}
            <div className="animate-fade-in-up" style={{ animationDelay: '0.2s', animationFillMode: 'forwards', opacity: 0, display: 'flex', gap: '20px', marginBottom: '32px' }}>
              <div className="animated-line" style={{ width: '4px', backgroundColor: '#8B0000', borderRadius: '2px', flexShrink: 0 }} />
              <h1 style={{ fontFamily: "'Cabinet Grotesk', sans-serif", fontWeight: 900, fontSize: 'clamp(2.8rem, 5.5vw, 5rem)', lineHeight: 1.05, letterSpacing: '-0.03em', margin: 0 }}>
                <span style={{ display: 'block', color: '#ffffff' }}>{titleWhite}</span>
                <span className="glitch-text" data-text={titleRed} style={{ display: 'block', color: '#8B0000' }}>{titleRed}</span>
              </h1>
            </div>

            {/* Animated underline */}
            <div className="animate-fade-in-up" style={{ animationDelay: '0.35s', animationFillMode: 'forwards', opacity: 0, marginBottom: '32px', marginLeft: '24px' }}>
              <div style={{ height: '2px', background: 'linear-gradient(90deg, #8B0000, transparent)', width: '120px', animation: 'lineExtend 1.2s ease-out 0.8s forwards', opacity: 0 }} />
            </div>

            {/* Buttons */}
            <div className="animate-fade-in-up" style={{ animationDelay: '0.5s', animationFillMode: 'forwards', opacity: 0, display: 'flex', flexWrap: 'wrap', gap: '12px' }}>
              <Link to="/demande-devis" data-testid="hero-cta-devis" className="btn-shimmer btn-ripple" onClick={handleRipple}
                style={{ backgroundColor: '#8B0000', color: '#fff', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.06em', fontSize: '13px', padding: '14px 28px', textDecoration: 'none', borderRadius: '100px', display: 'inline-flex', alignItems: 'center', gap: '8px' }}>
                {CTA_DEVIS} <ArrowRight size={16} />
              </Link>
              <a href="tel:+32466407988" className="btn-shimmer btn-ripple" onClick={handleRipple}
                style={{ border: '2px solid rgba(255,255,255,0.25)', color: '#fff', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.06em', fontSize: '13px', padding: '12px 24px', textDecoration: 'none', borderRadius: '100px', display: 'inline-flex', alignItems: 'center', gap: '8px', background: 'transparent' }}>
                <Phone size={16} /> +32 466 407 988
              </a>
            </div>
          </div>

          {/* RIGHT — Photo */}
          <div className="animate-fade-in-up" style={{ animationDelay: '0.4s', animationFillMode: 'forwards', opacity: 0 }}>
            <div style={{ position: 'relative' }}>
              <div style={{ overflow: 'hidden', borderRadius: '20px' }}>
                <img
                  ref={photoRef}
                  src={photo}
                  alt={photoAlt}
                  fetchPriority="high"
                  width="700"
                  height="933"
                  className="photo-premium"
                  style={{ width: '100%', aspectRatio: '3/4', objectFit: 'cover', borderRadius: '20px', display: 'block', willChange: 'transform' }}
                />
              </div>
              {/* Gradient overlay on photo bottom */}
              <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: '40%', borderRadius: '0 0 20px 20px', background: 'linear-gradient(to top, rgba(0,0,0,0.6), transparent)', pointerEvents: 'none' }} />
              {/* Floating Badge */}
              {BadgeIcon && (
                <div className="badge-float" style={{ position: 'absolute', bottom: '24px', left: '24px', backgroundColor: 'rgba(139,0,0,0.85)', borderRadius: '12px', padding: '14px 20px', display: 'flex', alignItems: 'center', gap: '10px', backdropFilter: 'blur(12px)', border: '1px solid rgba(139,0,0,0.3)' }}>
                  <BadgeIcon size={22} color="#fff" />
                  <div>
                    <span style={{ color: '#fff', fontWeight: 900, fontSize: '18px', fontFamily: "'Cabinet Grotesk', sans-serif", display: 'block', lineHeight: 1.2 }}>{photoBadgeTitle}</span>
                    <span style={{ color: 'rgba(255,255,255,0.7)', fontSize: '11px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.1em' }}>{photoBadgeSub}</span>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Wave divider */}
      <WaveDivider />
    </section>
  );
}
